import React, { ReactNode, createContext, useCallback, useContext, useState } from "react";
import ConfirmationDialog from "./ConfirmationDialog";

type ConfirmationProps = {
  title: string;
  text: string;
  confirmButton?: string;
  closeButton?: string;
  severity?: "neutral" | "error";
  onConfirm?: () => void;
  onClose?: () => void;
};

type ConfirmationAction = (props: ConfirmationProps) => void;

const ConfirmationContext = createContext<ConfirmationAction | null>(null);

export const useConfirmation = () => {
  const context = useContext(ConfirmationContext);

  if (!context) {
    throw new Error("Component must be wrapped in a ConfirmationProvider.");
  }

  const confirm = useCallback((props: ConfirmationProps) => { 
    context(props);
  }, [context]);

  return confirm;
};

type ConfirmationProviderProps = {
  children: ReactNode;
};

const ConfirmationProvider = ({ children }: ConfirmationProviderProps) => {
  const [confirmationProps, setConfirmationProps] = useState<ConfirmationProps | null>(null);
  const [open, setOpen] = useState(false);
  
  const handleShowDialog = useCallback((props: ConfirmationProps) => {
    setConfirmationProps(props);
    setOpen(true);
  }, []);

  const handleConfirm = () => {
    setOpen(false);
    if (confirmationProps?.onConfirm) {
      confirmationProps.onConfirm();
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (confirmationProps?.onClose) {
      confirmationProps.onClose();
    }
  };

  return (
    <ConfirmationContext.Provider value={handleShowDialog}>
      {children}

      <ConfirmationDialog {...confirmationProps} open={open} onConfirm={handleConfirm} onClose={handleClose} />
    </ConfirmationContext.Provider>
  );
}

export default ConfirmationProvider;